import type { Env } from "./env";

/**
 * Invite codes. This is the entire "keep strangers out" mechanism.
 *
 * There is deliberately no self-serve signup and no email provider: an admin mints a
 * code here, copies the join link, and sends it to a friend however they like. For a
 * handful of users that removes a whole dependency (and a domain-verification chore)
 * without giving anything up.
 */

export interface Invite {
  code: string;
  email: string | null;
  role: "user" | "admin";
  createdBy: string | null;
  createdAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  redeemedBy: string | null;
}

/** Unambiguous alphabet: no O/0, I/1, S/5 — these get read aloud and typed by hand. */
const ALPHABET = "ABCDEFGHJKLMNPQRTUVWXYZ2346789";

export function generateCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const chars = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]);
  // Grouped for legibility when read off a screen: ABCD-EFGH-JKLM
  return [chars.slice(0, 4), chars.slice(4, 8), chars.slice(8, 12)]
    .map((g) => g.join(""))
    .join("-");
}

export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export type InviteStatus = "open" | "expired" | "used";

/**
 * An invite's status, decided on the server from the RAW timestamps.
 *
 * The admin view used to derive this in render, from the already-formatted
 * `expiresAt` — and the display format carries no year, so `new Date("15 Aug")`
 * parsed as the year 2001 and every unredeemed invite showed "expired". Doing
 * it here also keeps `Date.now()` out of render, which the hydration rule
 * forbids (CLAUDE.md).
 */
export function inviteStatus(inv: Invite, nowMs: number = Date.now()): InviteStatus {
  if (inv.redeemedAt) return "used";
  return new Date(inv.expiresAt).getTime() < nowMs ? "expired" : "open";
}

/** Compare codes ignoring the cosmetic dashes, so a hand-typed code still works. */
function codeMatches(a: string, b: string): boolean {
  return normalizeCode(a) === normalizeCode(b);
}

type InviteRow = {
  code: string;
  email: string | null;
  role: string;
  created_by: string | null;
  created_at: string;
  expires_at: string;
  redeemed_at: string | null;
  redeemed_by: string | null;
};

const toInvite = (r: InviteRow): Invite => ({
  code: r.code,
  email: r.email,
  role: r.role === "admin" ? "admin" : "user",
  createdBy: r.created_by,
  createdAt: r.created_at,
  expiresAt: r.expires_at,
  redeemedAt: r.redeemed_at,
  redeemedBy: r.redeemed_by,
});

export type InviteRejection =
  | { ok: true; invite: Invite }
  | { ok: false; reason: "not_found" | "redeemed" | "expired" | "wrong_email" };

/**
 * Validate a code without consuming it. Called twice: once on GET /join so a bad link
 * fails immediately rather than after the user has filled in a password, and again in
 * the sign-up before-hook — because the first check is advisory and the client controls
 * the form.
 */
export async function checkInvite(
  env: Env,
  rawCode: string,
  email?: string,
): Promise<InviteRejection> {
  const code = normalizeCode(rawCode);
  if (!code) return { ok: false, reason: "not_found" };

  // Codes are stored with dashes; match on the normalized form so typing/pasting either works.
  const rows = await env.IDENTITY_DB.prepare(
    `SELECT code, email, role, created_by, created_at, expires_at, redeemed_at, redeemed_by
       FROM app_invitation
      WHERE replace(upper(code), '-', '') = ?`,
  )
    .bind(code)
    .all<InviteRow>();

  const row = rows.results?.[0];
  if (!row) return { ok: false, reason: "not_found" };

  const invite = toInvite(row);
  if (invite.redeemedAt) return { ok: false, reason: "redeemed" };
  if (new Date(invite.expiresAt).getTime() < Date.now()) return { ok: false, reason: "expired" };
  if (invite.email && email && invite.email.toLowerCase() !== email.toLowerCase()) {
    return { ok: false, reason: "wrong_email" };
  }
  return { ok: true, invite };
}

export const rejectionMessage = (reason: Exclude<InviteRejection, { ok: true }>["reason"]) =>
  ({
    not_found: "That invite code isn't valid.",
    redeemed: "That invite has already been used.",
    expired: "That invite has expired. Ask for a new one.",
    wrong_email: "This invite was issued for a different email address.",
  })[reason];

/**
 * Consume an invite. The `redeemed_at IS NULL` predicate makes this a compare-and-set,
 * so two people racing the same code cannot both win — `meta.changes` tells us who did.
 */
export async function redeemInvite(
  env: Env,
  rawCode: string,
  userId: string,
): Promise<boolean> {
  const result = await env.IDENTITY_DB.prepare(
    `UPDATE app_invitation
        SET redeemed_at = ?, redeemed_by = ?
      WHERE replace(upper(code), '-', '') = ?
        AND redeemed_at IS NULL`,
  )
    .bind(new Date().toISOString(), userId, normalizeCode(rawCode))
    .run();

  return (result.meta.changes ?? 0) === 1;
}

export async function createInvite(
  env: Env,
  opts: { email?: string | null; role?: "user" | "admin"; createdBy: string; ttlDays?: number },
): Promise<Invite> {
  const code = generateCode();
  const now = new Date();
  const expires = new Date(now.getTime() + (opts.ttlDays ?? 14) * 86_400_000);

  await env.IDENTITY_DB.prepare(
    `INSERT INTO app_invitation (code, email, role, created_by, created_at, expires_at)
     VALUES (?, ?, ?, ?, ?, ?)`,
  )
    .bind(
      code,
      opts.email?.trim().toLowerCase() || null,
      opts.role ?? "user",
      opts.createdBy,
      now.toISOString(),
      expires.toISOString(),
    )
    .run();

  return {
    code,
    email: opts.email?.trim().toLowerCase() || null,
    role: opts.role ?? "user",
    createdBy: opts.createdBy,
    createdAt: now.toISOString(),
    expiresAt: expires.toISOString(),
    redeemedAt: null,
    redeemedBy: null,
  };
}

export async function listInvites(env: Env): Promise<Invite[]> {
  const rows = await env.IDENTITY_DB.prepare(
    `SELECT code, email, role, created_by, created_at, expires_at, redeemed_at, redeemed_by
       FROM app_invitation
      ORDER BY created_at DESC
      LIMIT 100`,
  ).all<InviteRow>();
  return (rows.results ?? []).map(toInvite);
}

export async function revokeInvite(env: Env, code: string): Promise<void> {
  await env.IDENTITY_DB.prepare(
    `DELETE FROM app_invitation
      WHERE replace(upper(code), '-', '') = ? AND redeemed_at IS NULL`,
  )
    .bind(normalizeCode(code))
    .run();
}

export const joinUrl = (origin: string, code: string) =>
  `${origin}/join?code=${encodeURIComponent(code)}`;
